<template>
  <div class="irp-contact-list" data-testid="irp-contact-list">
    <BittsEmptyState
      v-if="!rows.length"
      class="irp-contact-list__empty"
      :font-awesome-icon="EMPTY_STATE.icon"
      :title="emptyMessage"
      :description="EMPTY_STATE.description"
      data-testid="irp-contact-list-empty"
    />
    <template v-else>
      <template v-for="(row, index) in rows" :key="`contact_${index}`">
        <div class="irp-contact-list__cell" data-testid="irp-contact-name">
          <span class="irp-contact-list__label">
            {{ row.name.label }}
          </span>
          <div class="irp-contact-list__name">
            <FontAwesomeIcon
              :icon="['fak', 'person']"
              class="irp-contact-list__icon"
            />
            <IRPFieldGridText :text="row.name.value" :is-masked="true" />
          </div>
        </div>

        <div class="irp-contact-list__cell" data-testid="irp-contact-role">
          <span class="irp-contact-list__label">
            {{ row.role.label }}
          </span>
          <IRPFieldGridText :text="row.role.value" :is-masked="true" />
        </div>

        <div class="irp-contact-list__cell" data-testid="irp-contact-email">
          <span class="irp-contact-list__label">
            {{ row.email.label }}
          </span>
          <a
            v-if="row.email.value !== EMPTY_FIELD_VALUE"
            :href="`mailto:${row.email.value}`"
            :aria-label="`Email ${row.name.value}`"
            class="irp-contact-list__email"
          >
            <IRPFieldGridText :text="row.email.value" :is-masked="true" />
          </a>
          <IRPFieldGridText v-else :text="EMPTY_FIELD_VALUE" />
        </div>

        <div class="irp-contact-list__cell" data-testid="irp-contact-status">
          <span class="irp-contact-list__label">
            {{ row.status.label }}
          </span>
          <IRPFieldGridText :text="row.status.value" />
        </div>

        <div
          v-if="row.remainingFields.length"
          class="irp-contact-list__all-fields"
        >
          <IRPAllFieldsCollapse :fields="row.remainingFields" />
        </div>

        <div
          v-if="index < rows.length - 1"
          class="irp-contact-list__separator"
        />
      </template>
    </template>
  </div>
</template>

<script setup lang="ts">
import { BittsEmptyState } from '@crossbeam/bitts';
import type { BittsIcon } from '@crossbeam/bitts';

import { computed } from 'vue';

import { MDM_PROPERTIES, MDM_TYPES } from '@/constants/mdm';
import type { RecordContactType } from '@/types/individual_record';

import { EMPTY_FIELD_VALUE, fieldCell, remainingFields } from './irp';
import IRPAllFieldsCollapse from './IRPAllFieldsCollapse.vue';
import IRPFieldGridText from './IRPFieldGridText.vue';

const {
  contacts,
  isOwnData = false,
  emptyMessage = 'No contacts to show',
} = defineProps<{
  contacts: RecordContactType[];
  isOwnData?: boolean;
  emptyMessage?: string;
}>();

const EMPTY_STATE: { description: string; icon: BittsIcon } = {
  description: 'Contacts for this account will appear here.',
  icon: ['fak', 'contacts'],
};

const CONTACT_PROPERTIES = MDM_PROPERTIES[MDM_TYPES.CONTACT];

const LISTED_PROPERTIES: string[] = [
  CONTACT_PROPERTIES.NAME,
  CONTACT_PROPERTIES.TITLE,
  CONTACT_PROPERTIES.EMAIL,
];

function statusOf(contact: RecordContactType) {
  if (isOwnData) return 'Known';
  const missingFromCrm = contact.missing_from_crm;
  if (missingFromCrm === undefined) return EMPTY_FIELD_VALUE;
  return missingFromCrm ? 'New' : 'Known';
}

const rows = computed(() =>
  contacts.map((contact) => ({
    name: fieldCell(contact.fields, CONTACT_PROPERTIES.NAME, 'Name'),
    role: fieldCell(contact.fields, CONTACT_PROPERTIES.TITLE, 'Role'),
    email: fieldCell(contact.fields, CONTACT_PROPERTIES.EMAIL, 'Email'),
    status: { label: 'Status', value: statusOf(contact) },
    remainingFields: remainingFields(contact.fields, LISTED_PROPERTIES),
  })),
);
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.irp-contact-list {
  @apply grid items-center gap-x-24 gap-y-16;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.irp-contact-list__empty {
  grid-column: 1 / -1;
}

.irp-contact-list__cell {
  @apply flex min-w-0 flex-col gap-4 text-base text-neutral-text;
}

.irp-contact-list__label {
  @apply truncate text-sm font-semibold text-neutral-text-weak;
}

.irp-contact-list__name {
  @apply flex min-w-0 items-center gap-8;
}

.irp-contact-list__icon {
  @apply shrink-0 text-neutral-accent;
}

.irp-contact-list__all-fields {
  grid-column: 1 / -1;
}

.irp-contact-list__separator {
  @apply border-t border-neutral-border;
  grid-column: 1 / -1;
}

.irp-contact-list__email {
  @apply min-w-0 text-secondary-text-link hover:text-secondary-text-link-hover;
}
</style>
